import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, Clock } from 'lucide-react';
import SEOScoreBadge from '@/components/marketplace/SEOScoreBadge';

const StatusPill = ({ p }) => {
  if (p.status === 'published') {
    return <span className='inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium status-approved' data-testid='blog-status-published'>Published</span>;
  }
  if (p.status === 'scheduled') {
    const when = p.scheduled_publish_at ? new Date(p.scheduled_publish_at) : null;
    const label = when ? when.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'pending';
    return (
      <span className='inline-flex items-center gap-1 rounded-full border text-[11px] px-2 py-0.5 font-medium bg-amber-500/10 text-amber-700 border-amber-500/30' data-testid='blog-status-scheduled'>
        <Clock className='h-2.5 w-2.5' /> Scheduled · {label}
      </span>
    );
  }
  return <span className='inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium status-pending' data-testid='blog-status-draft'>Draft</span>;
};

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/blog/posts', { params: { status: 'all', limit: 100 } }),
        api.get('/blog/categories', { params: { include_inactive: true } })
      ]);
      setPosts(p.data.items); setCats(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try { await api.delete(`/blog/posts/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className='space-y-5'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight'>Blog</h1>
          <p className='text-muted-foreground text-sm'>{posts.length} posts · {cats.length} categories</p>
        </div>
        <Link to='/admin/blog/new'>
          <Button data-testid='admin-new-post'><Plus className='h-4 w-4 mr-1' /> New post</Button>
        </Link>
      </div>

      <Card className='p-4'>
        {loading ? <div className='py-10 text-center text-muted-foreground'>Loading…</div> : posts.length === 0 ? (
          <div className='py-10 text-center text-muted-foreground'>No posts yet.</div>
        ) : (
          <table className='w-full text-sm'>
            <thead className='text-left'>
              <tr className='border-b'>
                <th className='py-2 pr-3'>Title</th>
                <th className='py-2 pr-3'>Category</th>
                <th className='py-2 pr-3'>Status</th>
                <th className='py-2 pr-3'>SEO</th>
                <th className='py-2 pr-3'>Views</th>
                <th className='py-2 pr-3'>Published</th>
                <th className='py-2 pr-3'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(p => (
                <tr key={p.id} className='border-b last:border-b-0 hover:bg-secondary/50' data-testid='admin-blog-row'>
                  <td className='py-2 pr-3 font-medium'>{p.title}</td>
                  <td className='py-2 pr-3 text-muted-foreground'>{p.category_name}</td>
                  <td className='py-2 pr-3'>
                    <StatusPill p={p} />
                  </td>
                  <td className='py-2 pr-3'>
                    <SEOScoreBadge score={p.seo_score} summary={p.seo_score_summary} />
                  </td>
                  <td className='py-2 pr-3 tabular-nums'>{p.view_count || 0}</td>
                  <td className='py-2 pr-3 text-muted-foreground' data-testid='admin-blog-publish-date'>
                    {p.published_at
                      ? new Date(p.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                      : (p.status === 'scheduled' && p.scheduled_publish_at
                          ? new Date(p.scheduled_publish_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—')}
                  </td>
                  <td className='py-2 pr-3'>
                    <div className='flex gap-1'>
                      <Link to={`/blog/${p.slug}`} target='_blank'><Button size='sm' variant='outline'><Eye className='h-3.5 w-3.5' /></Button></Link>
                      <Link to={`/admin/blog/${p.id}/edit`}><Button size='sm' variant='outline' data-testid={`admin-edit-${p.id}`}><Pencil className='h-3.5 w-3.5' /></Button></Link>
                      <Button size='sm' variant='outline' onClick={() => del(p.id)} data-testid={`admin-delete-${p.id}`}><Trash2 className='h-3.5 w-3.5' /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
};

export default AdminBlog;
